import Progress from 'antd/es/progress'
import { isDisabledIdeaState } from '@/enums/management-input.enum.ts'
import './filling-progress.scss'

export interface FillingProgressProps {
  /** Fill percentage as a 0-1 decimal (will be multiplied by 100 internally) */
  value: number | null | undefined
  /** The state of the parent idea (used to determine disabled styling) */
  ideaState: string | null | undefined
  /** The state of the order row itself (used for "Filled but <100%" check) */
  orderState?: string | null
  /** Whether this is an order-level row (enables the Filled check) */
  isOrderRow?: boolean
  /** Optional className passed to the antd Progress component */
  className?: string
}

/**
 * Shared filling-progress bar used in both the ag-grid table view and the
 * mobile card view. Encapsulates all color/disabled/filled logic in one place.
 */
export const FillingProgress: React.FC<FillingProgressProps> = ({
  value,
  ideaState,
  orderState,
  isOrderRow = false,
  className,
}) => {
  const percent = (value ?? 0) * 100

  if (typeof percent !== 'number' || isNaN(percent)) return null

  const isDisabled = isDisabledIdeaState(ideaState)
  // Order rows whose own state is "Canceled" get the same greyed-out styling
  const isOrderCanceled = isOrderRow && orderState === 'Canceled'
  // Order rows whose own state is "Filled" but haven't reached 100% get the
  // same greyed-out styling as disabled ideas.
  const isFilled = isOrderRow && orderState === 'Filled' && percent < 100
  const useDisabledStyle = isDisabled || isOrderCanceled || isFilled
  const isComplete = percent >= 100 && !isDisabled

  return (
    <Progress
      percent={percent}
      steps={5}
      size={'small'}
      status={isDisabled ? 'exception' : isComplete ? 'success' : 'active'}
      strokeColor={
        useDisabledStyle
          ? 'var(--ant-color-text-disabled)'
          : isComplete
            ? 'var(--ant-color-success)'
            : 'var(--ant-color-info)'
      }
      format={() =>
        useDisabledStyle ? (
          <span
            className={
              'filling-progress__text filling-progress__text--disabled'
            }
          >
            {Math.round(percent * 10) / 10}%
          </span>
        ) : (
          <span className={'filling-progress__text'}>
            {Math.round(percent * 10) / 10}%
          </span>
        )
      }
      className={className}
    />
  )
}
